import { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react'
import { ThemeProvider as MuiThemeProvider, createTheme, Theme } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { useMediaQuery } from '@mui/material'

type ThemeMode = 'light' | 'dark' | 'system'

interface ThemeContextType {
  mode: ThemeMode
  theme: Theme
  toggleMode: () => void
  setMode: (mode: ThemeMode) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const useThemeMode = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useThemeMode must be used within ThemeProvider')
  }
  return context
}

interface ThemeProviderProps {
  children: ReactNode
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)')
  const [mode, setModeState] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem('theme-mode')
    return (saved as ThemeMode) || 'system'
  })

  const getEffectiveMode = (): 'light' | 'dark' => {
    if (mode === 'system') {
      return prefersDarkMode ? 'dark' : 'light'
    }
    return mode
  }

  const effectiveMode = getEffectiveMode()

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: effectiveMode,
          primary: { main: '#2563eb' },
          secondary: { main: '#7c3aed' },
          success: { main: '#16a34a' },
          warning: { main: '#f59e0b' },
          error: { main: '#dc2626' },
          ...(effectiveMode === 'dark'
            ? {
                background: {
                  default: '#0f172a',
                  paper: '#111827',
                },
              }
            : {
                background: {
                  default: '#f8fafc',
                  paper: '#ffffff',
                },
              }),
        },
        shape: { borderRadius: 12 },
        typography: {
          fontFamily: ['Inter', '-apple-system', 'BlinkMacSystemFont', '"Segoe UI"', 'Roboto', 'Arial', 'sans-serif'].join(','),
          h6: { fontWeight: 700, letterSpacing: '-0.01em' },
          subtitle2: { fontWeight: 600 },
        },
        components: {
          MuiCard: {
            defaultProps: { elevation: 0 },
            styleOverrides: {
              root: {
                border: effectiveMode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(15,23,42,0.08)',
                boxShadow:
                  effectiveMode === 'dark'
                    ? '0 10px 25px rgba(2,6,23,0.35)'
                    : '0 10px 25px rgba(15,23,42,0.08)',
              },
            },
          },
          MuiCardContent: {
            styleOverrides: {
              root: {
                padding: 20,
                '&:last-child': { paddingBottom: 20 },
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: { textTransform: 'none', borderRadius: 10, fontWeight: 600 },
              containedPrimary: { boxShadow: 'none' },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: { fontWeight: 600 },
            },
          },
          MuiTableHead: {
            styleOverrides: {
              root: {
                '& .MuiTableCell-root': {
                  fontWeight: 700,
                  backgroundColor: effectiveMode === 'dark' ? 'rgba(148,163,184,0.12)' : 'rgba(15,23,42,0.04)',
                },
              },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: { borderRadius: 10 },
            },
          },
          MuiAlert: {
            styleOverrides: {
              root: {
                borderRadius: 10,
              },
            },
          },
        },
      }),
    [effectiveMode]
  )

  useEffect(() => {
    localStorage.setItem('theme-mode', mode)
  }, [mode])

  const setMode = (newMode: ThemeMode) => {
    setModeState(newMode)
  }

  const toggleMode = () => {
    setModeState((prev) => {
      if (prev === 'light') return 'dark'
      if (prev === 'dark') return 'system'
      return 'light'
    })
  }

  return (
    <ThemeContext.Provider value={{ mode, theme, toggleMode, setMode }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  )
}


